// src/App.js
// Updated: Tuesday, August 4, 2026 at 8:25 PM EDT

import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { CssBaseline } from '@mui/material';

// Import Pages
import Login from './pages/Login';
import Register from './pages/Register';
import Profile from './pages/Profile';
import Announcements from './pages/Announcements';
import PrayerWall from './pages/PrayerWall';
import Directory from './pages/Directory';
import Chat from './pages/Chat';
import Calendar from './pages/Calendar';
import UserManagement from './pages/UserManagement';
import ChurchManagement from './pages/ChurchManagement';
import GroupsList from './pages/GroupsList';
import GroupDetail from './pages/GroupDetail';
import GroupForm from './pages/GroupForm';
import VolunteerOpportunitiesPage from './pages/VolunteerOpportunitiesPage';
import VolunteerOpportunityForm from './pages/VolunteerOpportunityForm';
import SermonArchivesPage from './pages/SermonArchivesPage';
import RegistrationEventsPage from './pages/RegistrationEventsPage';
import EventRegistrations from './pages/EventRegistrations';
import SendPushNotificationPage from './pages/SendPushNotificationPage';
import HelpPage from './pages/HelpPage';
import SentNotificationsHistory from './pages/SentNotificationsHistory';
import JoinChurchPage from './pages/JoinChurchPage';
import NotificationsPage from './pages/NotificationsPage';
import HomePage from './pages/HomePage';
import ContactUsPage from './pages/ContactUsPage';
import InquiriesPage from './pages/InquiriesPage';
import WaysToGivePage from './pages/WaysToGivePage';
import EmailSettingsPage from './pages/EmailSettingsPage';
import NewsletterComposerPage from './pages/NewsletterComposerPage';
import EventReminderSettingsPage from './pages/EventReminderSettingsPage';
import DistributionListsPage from './pages/DistributionListsPage';
import VolunteerSignupsPage from './pages/VolunteerSignupsPage';
import VolunteerHistoryPage from './pages/VolunteerHistoryPage';
import VolunteerReportPage from './pages/VolunteerReportPage';
import AdminDashboard from './pages/AdminDashboard';
import BibleStudyArchivePage from './pages/BibleStudyArchivePage';
import TestimoniesPage from './pages/TestimoniesPage';

// FINANCIAL IMPORTS
import FinancialsPage from './pages/FinancialsPage'; 
import BatchApprovalQueue from './pages/financials/BatchApprovalQueue'; // Correct Path

// Import Admin Components
import PushTokenAdmin from './components/admin/PushTokenAdmin'; // Added PushTokenAdmin

// Import Core Components
import ProtectedRoute from './components/ProtectedRoute';
import AdminRoute from './components/AdminRoute';
import ApprovedUserRoute from './components/ApprovedUserRoute';
import FinanceRoute from './components/FinanceRoute';
import Layout from './components/Layout';
import { AuthProvider } from './context/AuthContext';

function App() {
  return (
    <>
      <CssBaseline />
      <Router>
        <AuthProvider>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }
            >
              {/* Routes accessible to ALL authenticated users */}
              <Route path="profile" element={<Profile />} />
              <Route path="help" element={<HelpPage />} />
              <Route path="join-church" element={<JoinChurchPage />} />
              <Route path="notifications" element={<NotificationsPage />} />
              <Route path="profile/volunteer-history" element={<ProtectedRoute><VolunteerHistoryPage /></ProtectedRoute>} />

              {/* Routes accessible ONLY to approved users with a church */}
              <Route index element={<ApprovedUserRoute><HomePage /></ApprovedUserRoute>} />
              <Route path="announcements" element={<ApprovedUserRoute><Announcements /></ApprovedUserRoute>} />
              <Route path="announcements/:announcementId" element={<ApprovedUserRoute><Announcements /></ApprovedUserRoute>} />
              <Route path="prayer-wall" element={<ApprovedUserRoute><PrayerWall /></ApprovedUserRoute>} />
              <Route path="testimonies" element={<ApprovedUserRoute><TestimoniesPage /></ApprovedUserRoute>} />
              <Route path="contact" element={<ApprovedUserRoute><ContactUsPage /></ApprovedUserRoute>} />
              <Route path="directory" element={<ApprovedUserRoute><Directory /></ApprovedUserRoute>} />
              <Route path="calendar" element={<ApprovedUserRoute><Calendar /></ApprovedUserRoute>} />
              <Route path="chat/:chatId" element={<ApprovedUserRoute><Chat /></ApprovedUserRoute>} />
              <Route path="groups" element={<ApprovedUserRoute><GroupsList /></ApprovedUserRoute>} />
              <Route path="groups/:groupId" element={<ApprovedUserRoute><GroupDetail /></ApprovedUserRoute>} />
              <Route path="volunteer" element={<ApprovedUserRoute><VolunteerOpportunitiesPage /></ApprovedUserRoute>} />
              <Route path="sermons" element={<ApprovedUserRoute><SermonArchivesPage /></ApprovedUserRoute>} />
              <Route path="bible-studies" element={<ApprovedUserRoute><BibleStudyArchivePage /></ApprovedUserRoute>} />
              <Route path="register-events" element={<ApprovedUserRoute><RegistrationEventsPage /></ApprovedUserRoute>} />
              <Route path="events/:eventId/registrations" element={<ApprovedUserRoute><EventRegistrations /></ApprovedUserRoute>} />
              <Route path="ways-to-give" element={<ApprovedUserRoute><WaysToGivePage /></ApprovedUserRoute>} />
              
              {/* FINANCIAL MODULE ROUTES */}
              <Route path="financials" element={<FinanceRoute><FinancialsPage /></FinanceRoute>} />
              <Route path="financials/queue" element={<FinanceRoute requiredLevel="admin"><BatchApprovalQueue /></FinanceRoute>} />
              {/* This route renders the FinancialsPage component but allows it to handle the ?batchId param */}
              <Route path="financials/batch-entry" element={<FinanceRoute requiredLevel="counter"><FinancialsPage /></FinanceRoute>} />

              <Route path="volunteer/:churchId/:opportunityId/manage" element={<ProtectedRoute><VolunteerSignupsPage /></ProtectedRoute>} />
              <Route path="volunteer/:opportunityId/edit" element={<ProtectedRoute><VolunteerOpportunityForm /></ProtectedRoute>} />

              {/* Admin Routes */}
              <Route path="admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
              <Route path="admin/inquiries" element={<AdminRoute><InquiriesPage /></AdminRoute>} />
              <Route path="groups/new" element={<AdminRoute><GroupForm /></AdminRoute>} />
              <Route path="groups/:groupId/edit" element={<AdminRoute><GroupForm /></AdminRoute>} />
              <Route path="admin/send-notification" element={<AdminRoute><SendPushNotificationPage /></AdminRoute>} />
              <Route path="admin/users" element={<AdminRoute><UserManagement /></AdminRoute>} />
              <Route path="admin/churches" element={<AdminRoute><ChurchManagement /></AdminRoute>} />
              <Route path="admin/notification-history" element={<AdminRoute><SentNotificationsHistory /></AdminRoute>} />
              <Route path="admin/email-settings" element={<AdminRoute><EmailSettingsPage /></AdminRoute>} />
              <Route path="admin/send-newsletter" element={<AdminRoute><NewsletterComposerPage /></AdminRoute>} />
              <Route path="admin/event-reminders" element={<AdminRoute><EventReminderSettingsPage /></AdminRoute>} />
              <Route path="admin/distribution-lists" element={<AdminRoute><DistributionListsPage /></AdminRoute>} />
              <Route path="admin/volunteer-reports" element={<AdminRoute><VolunteerReportPage /></AdminRoute>} />
              <Route path="volunteer/new" element={<AdminRoute><VolunteerOpportunityForm /></AdminRoute>} />
              <Route path="admin/devices" element={<AdminRoute><PushTokenAdmin /></AdminRoute>} /> {/* Added Device Management Route */}

            </Route>

            {/* Catch-all route */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AuthProvider>
      </Router>
    </>
  );
}

export default App;